import { ofType } from 'redux-observable';
import { mergeMap, map, filter, of } from 'rxjs';
import { isEmpty, isNil } from 'lodash';
import { AppEpic } from 'src/store';
import {
  inputIsNotNullOrUndefined,
  maybeGetComponentProps,
  topMemInWorklistSelected,
} from 'src/utils/Functions/epicsFunctions';
import { ConfDefnComponentType, type NestedOvertimeComponent } from 'configuration-schemas/confdefnComponents';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { setActivePage } from 'src/pages/NavigationShell/NavigationShell.slice';
import { ListDataOptions } from 'src/worker/pivotWorker.types';
import { receiveScopeRefreshTrigger } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import {
  fetchNestedOvertimeData,
  receiveTenantConfig,
  setConfigureSelections,
  setTopMember,
} from './NestedOvertime.slice';
import { LENS_ASST_PLAN_REFRESHING_PAGE } from '../../Planning.types';
import { isNotBlacklistedByDataIndex } from 'src/utils/Component/AttributeBlacklist.utils';

export const nestedOvertimeLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      receiveScopeRefreshTrigger.type,
      receiveFilterStateAfterSubmission.type,
      setActivePage.type,
      receiveTenantConfig.type,
      setTopMember.type,
      setConfigureSelections.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
    ),
    map(() => maybeGetComponentProps<NestedOvertimeComponent>(state$.value, ConfDefnComponentType.nestedOvertime)),
    filter(inputIsNotNullOrUndefined),
    filter(() => topMemInWorklistSelected(state$.value)),
    // filter(() => isScopeDefined(state$.value.scope) && isSubheaderLoaded(state$.value.subheader)),
    // @ts-ignore
    mergeMap(({ dataApi }) => {
      const overtimeDefn = dataApi.params.defnId;
      const { topMember, configureSelections, transposedSelections } = state$.value.pages.hindsighting.nestedOvertime;

      if (!isEmpty(configureSelections)) {
        let overtimeOptions: ListDataOptions;
        if (!isNil(transposedSelections)) {
          overtimeOptions = {
            ...dataApi.params,
            topMembers: topMember,
            aggBy: [`level:${transposedSelections[1].dataIndex}`]
              .concat(configureSelections.map((i) => `${i.type}:${i.dataIndex}`))
              .join(','),
          };
        } else {
          overtimeOptions = {
            ...dataApi.params,
            topMembers: topMember,
            aggBy: [dataApi.params.aggBy].concat(configureSelections.map((i) => `${i.type}:${i.dataIndex}`)).join(','),
          };
        }
        // overtime data request needs a topmember which comes from companionData selection
        return isEmpty(topMember) && of(fetchNestedOvertimeData(overtimeDefn, overtimeOptions));
      } else return of();
    }),
  );
};

/** This is here to correct aggBys state if the user scopes to a department
 * in which one of the `aggBys` is blacklisted in configure.
 * It attempts to check for all the relevant config and keys and if an `aggBys`
 * is blacklisted, then just returns defaults again
 */
export const nestedOverTimePruneBlacklistedAggBys: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(receiveScopeRefreshTrigger.type),
    map(() => {
      const { attributeBlacklist } = state$.value.subheader;
      const currentDepartment = state$.value.scope.scope.productMember;
      const { configureSelections, viewDefn } = state$.value.pages.hindsighting.nestedOvertime;
      return { attributeBlacklist, currentDepartment, configureSelections, viewDefn };
    }),
    filter(
      ({ attributeBlacklist, currentDepartment, configureSelections }) =>
        !isEmpty(attributeBlacklist) && !isEmpty(currentDepartment) && !isEmpty(configureSelections),
    ),
    filter(({ attributeBlacklist, currentDepartment, configureSelections }) => {
      if (!attributeBlacklist || !currentDepartment) return false;
      const keep = isNotBlacklistedByDataIndex(attributeBlacklist, currentDepartment);
      return configureSelections.some((sel) => !keep({ dataIndex: sel.dataIndex }));
    }),
    map(({ viewDefn }) => {
      const defaults = viewDefn.configure.defaults.map((key, ind) => {
        const options = viewDefn.configure.view[ind].options;
        return options.find((i) => i.dataIndex === key) ?? options[0];
      });
      return setConfigureSelections(defaults);
    }),
  );
};
